import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings, Upload, Save, Palette, Type, Image, Globe, Mail, Phone, MapPin, HelpCircle, Shield, FileText, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const API = process.env.REACT_APP_BACKEND_URL || '';

function SubscriptionFAQManager({ token }) {
  const [faqs, setFaqs] = useState([]);
  const [newQ, setNewQ] = useState('');
  const [newA, setNewA] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchFaqs = async () => {
    try {
      const res = await fetch(`${API}/api/admin/subscription/faqs`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setFaqs(await res.json());
    } catch { /* */ }
  };
  useEffect(() => { fetchFaqs(); }, []);

  const addFaq = async () => {
    if (!newQ.trim() || !newA.trim()) return toast.error('Both question and answer are required');
    setSaving(true);
    try {
      await fetch(`${API}/api/admin/subscription/faqs`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: newQ.trim(), answer: newA.trim(), sort_order: faqs.length })
      });
      setNewQ(''); setNewA('');
      fetchFaqs();
      toast.success('FAQ added');
    } catch { toast.error('Failed to add'); } finally { setSaving(false); }
  };

  const deleteFaq = async (id) => {
    await fetch(`${API}/api/admin/subscription/faqs/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    fetchFaqs();
    toast.success('FAQ removed');
  };

  return (
    <Card className="border-stone-200">
      <CardContent className="p-6">
        <h3 className="text-lg font-bold text-stone-800 mb-3">Subscription Page FAQs</h3>
        <p className="text-xs text-stone-500 mb-4">These appear on the subscription/pricing page. If none are added, default FAQs will show.</p>
        <div className="space-y-2 mb-4">
          {faqs.map(faq => (
            <div key={faq.id} className="flex items-start gap-2 bg-stone-50 rounded-lg p-3 border border-stone-200">
              <div className="flex-1">
                <p className="text-sm font-medium text-stone-800">{faq.question}</p>
                <p className="text-xs text-stone-500">{faq.answer}</p>
              </div>
              <button onClick={() => deleteFaq(faq.id)} className="text-red-400 hover:text-red-600 p-1"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="space-y-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <Input value={newQ} onChange={e => setNewQ(e.target.value)} placeholder="Question" className="text-sm" />
          <Textarea value={newA} onChange={e => setNewA(e.target.value)} placeholder="Answer" rows={2} className="text-sm" />
          <Button onClick={addFaq} disabled={saving} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add FAQ
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

const AdminSettings = () => {
  const navigate = useNavigate();
  const { token, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  
  const [sendingTest, setSendingTest] = useState(null);
  const [testEmailAddr, setTestEmailAddr] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateData, setTemplateData] = useState({
    verification_subject: '',
    verification_body: '',
    welcome_subject: '',
    welcome_body: '',
    reset_subject: '',
    reset_body: '',
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  const sendTestEmail = async (type) => {
    const emailTo = testEmailAddr || settings.contact_email;
    if (!emailTo) return toast.error('Enter an email address first');
    setSendingTest(type);
    try {
      const res = await fetch(`${API}/api/admin/test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ to: emailTo, type })
      });
      const data = await res.json();
      if (res.ok) toast.success(data.message || `Test ${type} email sent!`);
      else toast.error(data.detail || 'Failed to send');
    } catch { toast.error('Failed to send test email'); }
    finally { setSendingTest(null); }
  };

  const quillModules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'color': ['#292524', '#57534E', '#78350F', '#D97706', '#dc2626', '#166534', '#1d4ed8'] }, { 'background': ['#FFFBEB', '#FEF2F2', '#F0FDF4', '#EFF6FF', '#F5F5F4', false] }],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['blockquote'],
      ['clean']
    ]
  }), []);

  const quillFormats = ['header', 'bold', 'italic', 'underline', 'color', 'background', 'list', 'bullet', 'blockquote'];

  const saveTemplate = async () => {
    setSavingTemplate(true);
    try {
      const res = await fetch(`${API}/api/admin/email-templates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(templateData)
      });
      if (res.ok) toast.success('Email templates saved!');
      else toast.error('Failed to save templates');
    } catch { toast.error('Failed to save'); }
    finally { setSavingTemplate(false); }
  };

  const [settings, setSettings] = useState({
    app_name: '',
    app_tagline: '',
    app_description: '',
    app_logo: '',
    app_favicon: '',
    primary_color: '#d97706',
    logo_max_height: 64,
    logo_header_height: 32,
    logo_login_height: 80,
    logo_shape: 'square',
    // Contact info fields
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    website_url: '',
    support_url: '',
    privacy_url: '',
    terms_url: '',
    // Menu visibility
    hidden_menus: []
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API}/api/admin/app-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings({
          app_name: data.app_name ?? '',
          app_tagline: data.app_tagline ?? '',
          app_description: data.app_description ?? '',
          app_logo: data.app_logo || '',
          app_favicon: data.app_favicon || '',
          primary_color: data.primary_color || '#d97706',
          logo_max_height: data.logo_max_height || 64,
          logo_header_height: data.logo_header_height || 32,
          logo_login_height: data.logo_login_height || 80,
          logo_shape: data.logo_shape || 'square',
          contact_email: data.contact_email ?? '',
          contact_phone: data.contact_phone ?? '',
          contact_address: data.contact_address ?? '',
          website_url: data.website_url ?? '',
          support_url: data.support_url ?? '',
          privacy_url: data.privacy_url ?? '',
          terms_url: data.terms_url ?? '',
          hidden_menus: data.hidden_menus || []
        });
      }
      // Load email templates
      const tplRes = await fetch(`${API}/api/admin/email-templates`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (tplRes.ok) {
        const tpl = await tplRes.json();
        setTemplateData(prev => ({ ...prev, ...tpl }));
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo must be less than 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({ ...prev, app_logo: data.file_id }));
        toast.success('Logo uploaded successfully!');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/app-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        toast.success('App settings saved successfully!');
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = () => {
    setSettings(prev => ({ ...prev, app_logo: '' }));
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (favicons are typically .ico, .png, or .svg)
    const validTypes = ['image/x-icon', 'image/vnd.microsoft.icon', 'image/png', 'image/svg+xml', 'image/ico'];
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file (.ico, .png, or .svg)');
      return;
    }

    // Validate file size (max 500KB for favicons)
    if (file.size > 500 * 1024) {
      toast.error('Favicon must be less than 500KB');
      return;
    }

    setUploadingFavicon(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(`${API}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({ ...prev, app_favicon: data.file_id }));
        toast.success('Favicon uploaded successfully!');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleRemoveFavicon = () => {
    setSettings(prev => ({ ...prev, app_favicon: '' }));
  };

  if (!isAdmin) {
    return (
      <div className="bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-stone-800">Access Denied</h1>
          <p className="text-stone-600 mt-2">You need admin privileges to access this page.</p>
          <Button onClick={() => navigate('/')} className="mt-4">Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <Settings className="h-12 w-12 text-amber-600 animate-spin mx-auto mb-4" />
          <p className="text-stone-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="honeycomb-bg p-4">
      {/* Header */}
      <div className="max-w-7xl mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
              <Settings className="h-6 w-6 text-amber-600" />
              App Settings
            </h1>
            <p className="text-sm text-stone-500 mt-1">Customize your app branding</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl space-y-6">
        {/* App Logo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Image className="h-5 w-5 text-amber-600" />
              App Logo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <div className="flex-shrink-0">
                {settings.app_logo ? (
                  <div className="relative group">
                    <img
                      src={`${API}/api/uploads/${settings.app_logo}`}
                      alt="App Logo"
                      className="w-48 h-48 object-contain rounded-xl border-2 border-stone-200 bg-white p-3"
                    />
                    <button
                      onClick={handleRemoveLogo}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="w-48 h-48 rounded-xl border-2 border-dashed border-stone-300 bg-stone-50 flex flex-col items-center justify-center text-stone-400">
                    <Image className="h-12 w-12 mb-2" />
                    <span className="text-sm">No logo</span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm text-stone-600 mb-3">
                  Upload your app logo. This will appear in the header, login page, and PDF exports.
                  Recommended size: 200x200px or larger. Max file size: 2MB.
                </p>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg cursor-pointer transition-colors">
                  <Upload className="h-4 w-4" />
                  {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={uploadingLogo}
                  />
                </label>

                {/* Logo Display Options */}
                <div className="mt-4 pt-4 border-t border-stone-200 space-y-4">
                  <p className="text-sm font-medium text-stone-700">Display Options</p>

                  {/* Context-specific sizes */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-stone-500 mb-1 block">Dashboard Header Size</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="range" min="20" max="56" step="4"
                          value={settings.logo_header_height}
                          onChange={e => setSettings(prev => ({ ...prev, logo_header_height: parseInt(e.target.value) }))}
                          className="flex-1 accent-amber-600"
                        />
                        <span className="text-sm font-medium text-amber-700 min-w-[3rem]">{settings.logo_header_height}px</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-stone-500 mb-1 block">Login / Landing Page Size</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="range" min="40" max="160" step="8"
                          value={settings.logo_login_height}
                          onChange={e => setSettings(prev => ({ ...prev, logo_login_height: parseInt(e.target.value) }))}
                          className="flex-1 accent-amber-600"
                        />
                        <span className="text-sm font-medium text-amber-700 min-w-[3rem]">{settings.logo_login_height}px</span>
                      </div>
                    </div>
                  </div>

                  {/* Shape */}
                  <div>
                    <label className="text-xs text-stone-500 mb-1 block">Logo Shape</label>
                    <div className="flex gap-2">
                      {[
                        { value: 'square', label: 'Square' },
                        { value: 'rounded', label: 'Rounded' },
                        { value: 'circle', label: 'Circle' },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setSettings(prev => ({ ...prev, logo_shape: opt.value }))}
                          className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${settings.logo_shape === opt.value ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-stone-600 border-stone-300 hover:border-amber-400'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Live Previews */}
                  {settings.app_logo && (
                    <div className="bg-stone-50 rounded-lg p-3 border border-stone-200 space-y-3">
                      <div>
                        <p className="text-[10px] text-stone-400 uppercase tracking-wider mb-1">Dashboard Header Preview</p>
                        <div className="flex items-center gap-2 bg-white rounded-lg p-2 border border-stone-100 inline-flex">
                          <img
                            src={`${API}/api/uploads/${settings.app_logo}`}
                            alt="Header preview"
                            style={{ maxHeight: `${settings.logo_header_height}px` }}
                            className={`object-contain ${settings.logo_shape === 'circle' ? 'rounded-full' : settings.logo_shape === 'rounded' ? 'rounded-lg' : ''}`}
                          />
                          {settings.app_name && <span className="text-sm font-bold text-stone-800">{settings.app_name}</span>}
                        </div>
                      </div>
                      <div>
                        <p className="text-[10px] text-stone-400 uppercase tracking-wider mb-1">Login Page Preview</p>
                        <div className="flex justify-center bg-white rounded-lg p-4 border border-stone-100">
                          <img
                            src={`${API}/api/uploads/${settings.app_logo}`}
                            alt="Login preview"
                            style={{ maxHeight: `${settings.logo_login_height}px` }}
                            className={`object-contain ${settings.logo_shape === 'circle' ? 'rounded-full' : settings.logo_shape === 'rounded' ? 'rounded-xl' : ''}`}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Favicon */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="h-5 w-5 text-amber-600" />
              Favicon
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start gap-6">
              <div className="flex-shrink-0">
                {settings.app_favicon ? (
                  <div className="relative group">
                    <img
                      src={`${API}/api/uploads/${settings.app_favicon}`}
                      alt="Favicon"
                      className="w-24 h-24 object-contain rounded-lg border-2 border-stone-200 bg-white p-2"
                    />
                    <button
                      onClick={handleRemoveFavicon}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 flex flex-col items-center justify-center text-stone-400">
                    <Globe className="h-8 w-8 mb-1" />
                    <span className="text-xs">No favicon</span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <p className="text-sm text-stone-600 mb-3">
                  Upload a favicon (the small icon shown in browser tabs). 
                  Recommended formats: .ico, .png, or .svg. Size: 32x32px or 64x64px. Max file size: 500KB.
                </p>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-800 rounded-lg cursor-pointer transition-colors">
                  <Upload className="h-4 w-4" />
                  {uploadingFavicon ? 'Uploading...' : 'Upload Favicon'}
                  <input
                    type="file"
                    accept=".ico,.png,.svg,image/x-icon,image/png,image/svg+xml"
                    onChange={handleFaviconUpload}
                    className="hidden"
                    disabled={uploadingFavicon}
                  />
                </label>
                <p className="text-xs text-stone-500 mt-2">
                  💡 Tip: After uploading, the favicon will appear in your browser tab after a page refresh.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* App Name & Tagline */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Type className="h-5 w-5 text-amber-600" />
              App Name & Tagline
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="app_name">App Name</Label>
              <Input
                id="app_name"
                value={settings.app_name}
                onChange={(e) => setSettings({ ...settings, app_name: e.target.value })}
                placeholder="e.g., Thrive"
                className="mt-1"
              />
              <p className="text-xs text-stone-500 mt-1">This appears in the browser tab and header</p>
            </div>
            <div>
              <Label htmlFor="app_tagline">Tagline</Label>
              <Input
                id="app_tagline"
                value={settings.app_tagline}
                onChange={(e) => setSettings({ ...settings, app_tagline: e.target.value })}
                placeholder="e.g., Beekeeping Management"
                className="mt-1"
              />
              <p className="text-xs text-stone-500 mt-1">Short description shown alongside the app name</p>
            </div>
            <div>
              <Label htmlFor="app_description">Description</Label>
              <Textarea
                id="app_description"
                value={settings.app_description}
                onChange={(e) => setSettings({ ...settings, app_description: e.target.value })}
                placeholder="Describe your app..."
                className="mt-1"
                rows={3}
              />
              <p className="text-xs text-stone-500 mt-1">Longer description used on the login page</p>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white rounded-xl p-6 border-2 border-stone-200">
              <div className="flex items-center gap-4">
                {settings.app_logo ? (
                  <img
                    src={`${API}/api/uploads/${settings.app_logo}`}
                    alt="Logo Preview"
                    className="w-16 h-16 object-contain"
                  />
                ) : (
                  <div className="w-16 h-16 bg-amber-100 rounded-lg flex items-center justify-center">
                    <span className="text-3xl">🐝</span>
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-stone-800">{settings.app_name || 'Thrive'}</h2>
                  <p className="text-sm text-stone-500">{settings.app_tagline || 'Beekeeping Management'}</p>
                </div>
              </div>
              {settings.app_description && (
                <p className="mt-4 text-stone-600 text-sm">{settings.app_description}</p>
              )}
              
              {/* Browser Tab Preview */}
              <div className="mt-6 pt-4 border-t border-stone-200">
                <p className="text-xs text-stone-500 mb-2">Browser Tab Preview:</p>
                <div className="inline-flex items-center gap-2 bg-stone-100 rounded-t-lg px-3 py-2 border border-b-0 border-stone-300">
                  {settings.app_favicon ? (
                    <img
                      src={`${API}/api/uploads/${settings.app_favicon}`}
                      alt="Favicon"
                      className="w-4 h-4 object-contain"
                    />
                  ) : (
                    <span className="text-xs">🐝</span>
                  )}
                  <span className="text-sm text-stone-700 truncate max-w-[150px]">{settings.app_name || 'Thrive'}</span>
                  <span className="text-stone-400 text-xs">×</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Info for Footer */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="h-5 w-5 text-amber-600" />
              Contact Information
            </CardTitle>
            <p className="text-sm text-stone-500">This information will be displayed in the app footer</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-stone-400" />
                  Contact Email
                </Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={settings.contact_email}
                  onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                  placeholder="support@yourcompany.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="contact_phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-stone-400" />
                  Contact Phone
                </Label>
                <Input
                  id="contact_phone"
                  type="tel"
                  value={settings.contact_phone}
                  onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="contact_address" className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-stone-400" />
                Address
              </Label>
              <Textarea
                id="contact_address"
                value={settings.contact_address}
                onChange={(e) => setSettings({ ...settings, contact_address: e.target.value })}
                placeholder="123 Bee Lane, Honey City, HC 12345"
                className="mt-1"
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Links for Footer */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Globe className="h-5 w-5 text-amber-600" />
              External Links
            </CardTitle>
            <p className="text-sm text-stone-500">Links displayed in the app footer</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="website_url" className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-stone-400" />
                  Website URL
                </Label>
                <Input
                  id="website_url"
                  type="url"
                  value={settings.website_url}
                  onChange={(e) => setSettings({ ...settings, website_url: e.target.value })}
                  placeholder="https://www.yourcompany.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="support_url" className="flex items-center gap-2">
                  <HelpCircle className="h-4 w-4 text-stone-400" />
                  Support/Help URL
                </Label>
                <Input
                  id="support_url"
                  type="url"
                  value={settings.support_url}
                  onChange={(e) => setSettings({ ...settings, support_url: e.target.value })}
                  placeholder="https://support.yourcompany.com"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="privacy_url" className="flex items-center gap-2">
                  <Shield className="h-4 w-4 text-stone-400" />
                  Privacy Policy URL
                </Label>
                <Input
                  id="privacy_url"
                  type="url"
                  value={settings.privacy_url}
                  onChange={(e) => setSettings({ ...settings, privacy_url: e.target.value })}
                  placeholder="https://www.yourcompany.com/privacy"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="terms_url" className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-stone-400" />
                  Terms of Service URL
                </Label>
                <Input
                  id="terms_url"
                  type="url"
                  value={settings.terms_url}
                  onChange={(e) => setSettings({ ...settings, terms_url: e.target.value })}
                  placeholder="https://www.yourcompany.com/terms"
                  className="mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Menu Visibility */}
        <Card className="border-stone-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Settings className="h-5 w-5 text-amber-600" />
              Menu Visibility
            </CardTitle>
            <p className="text-sm text-stone-500">Toggle menu items on or off for all users. Hidden items are still accessible to admins.</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { key: 'merch_store', label: 'Merch Store', description: 'Merchandise store for selling branded products' },
              ].map(item => {
                const isHidden = (settings.hidden_menus || []).includes(item.key);
                return (
                  <div key={item.key} className="flex items-center justify-between p-3 rounded-lg border border-stone-200 hover:border-stone-300 transition-colors" data-testid={`menu-toggle-${item.key}`}>
                    <div>
                      <p className="font-medium text-stone-800 text-sm">{item.label}</p>
                      <p className="text-xs text-stone-500">{item.description}</p>
                    </div>
                    <button
                      onClick={() => {
                        const menus = settings.hidden_menus || [];
                        setSettings({
                          ...settings,
                          hidden_menus: isHidden ? menus.filter(k => k !== item.key) : [...menus, item.key]
                        });
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isHidden ? 'bg-stone-300' : 'bg-green-500'}`}
                      data-testid={`menu-toggle-switch-${item.key}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isHidden ? 'translate-x-1' : 'translate-x-6'}`} />
                    </button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Email Preview / Test */}
        <Card className="border-stone-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="h-5 w-5 text-amber-600" />
              Email Preview & Testing
            </CardTitle>
            <p className="text-sm text-stone-500">Send yourself a test email to preview how branded emails look in your inbox</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-sm text-stone-600 mb-1 block">Send test emails to:</Label>
              <Input
                type="email"
                value={testEmailAddr}
                onChange={e => setTestEmailAddr(e.target.value)}
                placeholder={settings.contact_email || 'your@email.com'}
                className="max-w-sm"
                data-testid="test-email-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Button
                variant="outline"
                onClick={() => sendTestEmail('verification')}
                disabled={!!sendingTest}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="test-verification-email"
              >
                {sendingTest === 'verification' ? 'Sending...' : 'Test Verification Email'}
              </Button>
              <Button
                variant="outline"
                onClick={() => sendTestEmail('welcome')}
                disabled={!!sendingTest}
                className="border-green-300 text-green-700 hover:bg-green-50"
                data-testid="test-welcome-email"
              >
                {sendingTest === 'welcome' ? 'Sending...' : 'Test Welcome Email'}
              </Button>
              <Button
                variant="outline"
                onClick={() => sendTestEmail('password_reset')}
                disabled={!!sendingTest}
                className="border-stone-300 text-stone-700 hover:bg-stone-50"
                data-testid="test-reset-email"
              >
                {sendingTest === 'password_reset' ? 'Sending...' : 'Test Password Reset'}
              </Button>
            </div>
            <p className="text-xs text-stone-400">Emails are sent using your Resend API key and will appear from {settings.contact_email || 'your configured sender address'}.</p>
          </CardContent>
        </Card>

        {/* Email Template Editor */}
        <Card className="border-stone-200">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="h-5 w-5 text-amber-600" />
              Email Templates
            </CardTitle>
            <p className="text-sm text-stone-500">Edit the text content of your automated emails. The branded header, logo, and styling are applied automatically.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Template tabs */}
            <div className="flex gap-1 border-b border-stone-200 pb-0">
              {[
                { key: 'verification', label: 'Verification' },
                { key: 'welcome', label: 'Welcome' },
                { key: 'reset', label: 'Password Reset' },
              ].map(t => (
                <button
                  key={t.key}
                  onClick={() => setEditingTemplate(editingTemplate === t.key ? null : t.key)}
                  data-testid={`email-tab-${t.key}`}
                  className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${editingTemplate === t.key ? 'bg-amber-50 text-amber-800 border-amber-500' : 'text-stone-500 hover:text-stone-700 border-transparent hover:bg-stone-50'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {editingTemplate === 'verification' && (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm text-stone-600">Subject Line</Label>
                  <Input value={templateData.verification_subject} onChange={e => setTemplateData({...templateData, verification_subject: e.target.value})} placeholder="Welcome to the Swarm! Confirm your Thrive account" className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm text-stone-600">Email Body</Label>
                  <div className="mt-1 email-editor">
                    <ReactQuill theme="snow" value={templateData.verification_body || ''} onChange={val => setTemplateData({...templateData, verification_body: val})} modules={quillModules} formats={quillFormats} placeholder="Thanks for creating an account with Thrive Beekeeping!..." />
                  </div>
                  <p className="text-xs text-stone-400 mt-1">Use <code className="bg-stone-100 px-1 rounded">{'{{name}}'}</code> for the user's name. The verify button and link are added automatically below your content.</p>
                </div>
              </div>
            )}

            {editingTemplate === 'welcome' && (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm text-stone-600">Subject Line</Label>
                  <Input value={templateData.welcome_subject} onChange={e => setTemplateData({...templateData, welcome_subject: e.target.value})} placeholder="You're in! Welcome to Thrive Beekeeping" className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm text-stone-600">Email Body</Label>
                  <div className="mt-1 email-editor">
                    <ReactQuill theme="snow" value={templateData.welcome_body || ''} onChange={val => setTemplateData({...templateData, welcome_body: val})} modules={quillModules} formats={quillFormats} placeholder="Welcome to Thrive! Whether you are here to streamline..." />
                  </div>
                  <p className="text-xs text-stone-400 mt-1">Use <code className="bg-stone-100 px-1 rounded">{'{{name}}'}</code> for the user's name. The dashboard button is added automatically below your content.</p>
                </div>
              </div>
            )}

            {editingTemplate === 'reset' && (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm text-stone-600">Subject Line</Label>
                  <Input value={templateData.reset_subject} onChange={e => setTemplateData({...templateData, reset_subject: e.target.value})} placeholder="Thrive Beekeeping — Password Reset" className="mt-1" />
                </div>
                <div>
                  <Label className="text-sm text-stone-600">Email Body</Label>
                  <div className="mt-1 email-editor">
                    <ReactQuill theme="snow" value={templateData.reset_body || ''} onChange={val => setTemplateData({...templateData, reset_body: val})} modules={quillModules} formats={quillFormats} placeholder="We received a request to reset your password..." />
                  </div>
                  <p className="text-xs text-stone-400 mt-1">Use <code className="bg-stone-100 px-1 rounded">{'{{name}}'}</code> for the user's name. The reset button and link are added automatically below your content.</p>
                </div>
              </div>
            )}

            {!editingTemplate && (
              <p className="text-sm text-stone-500 py-4 text-center">Click a tab above to edit that email template</p>
            )}

            {editingTemplate && (
              <div className="flex gap-3 pt-2">
                <Button onClick={saveTemplate} disabled={savingTemplate} className="bg-amber-600 hover:bg-amber-700 text-white">
                  {savingTemplate ? 'Saving...' : 'Save Templates'}
                </Button>
                <Button variant="outline" onClick={() => sendTestEmail(editingTemplate)} disabled={!!sendingTest} className="border-stone-300">
                  {sendingTest ? 'Sending...' : 'Send Test'}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Subscription FAQ Manager */}
        <SubscriptionFAQManager token={token} />

        {/* Contact Email */}
        <Card className="border-stone-200">
          <CardContent className="p-6">
            <h3 className="text-lg font-bold text-stone-800 mb-3">Contact Email</h3>
            <p className="text-xs text-stone-500 mb-2">This email appears on the subscription page "Contact us" link and other public-facing pages.</p>
            <div className="flex gap-2">
              <input
                value={settings.contact_email || ''}
                onChange={e => setSettings({ ...settings, contact_email: e.target.value })}
                placeholder="marlene@thrivebeekeeping.com"
                className="flex-1 border border-stone-300 rounded-lg h-10 px-3 text-sm"
              />
              <Button onClick={handleSave} className="bg-amber-600 hover:bg-amber-700 text-white">Save</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSettings;
